import { useMemo, useCallback } from "react";
import { AreaClosed, LinePath, Bar } from "@visx/shape";
import { curveMonotoneX } from "@visx/curve";
import { scaleTime, scaleLinear } from "@visx/scale";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { useTooltip } from "@visx/tooltip";
import { localPoint } from "@visx/event";
import { bisector } from "d3-array";
import { ChartTooltip } from "../../../shared/components/ChartTooltip/ChartTooltip";
import type { McpInteractionTimeseriesResponse } from "../../types";
import {
  formatNumber,
  formatCompact,
  formatShortDate,
} from "../../utils/formatters";
import styles from "./McpInteractionTimeseriesChart.module.css";

type DataPoint = McpInteractionTimeseriesResponse["data_points"][number];

const getDate = (d: DataPoint) => new Date(d.period_start + "T00:00:00Z");
const getValue = (d: DataPoint) => d.interactions;
const bisectDate = bisector<DataPoint, Date>((d) => getDate(d)).left;

const MARGIN = { top: 16, right: 16, bottom: 40, left: 56 };
const LINE_COLOR = "#14b8a6";
const AREA_COLOR = "rgba(20, 184, 166, 0.1)";

interface InnerChartProps {
  dataPoints: DataPoint[];
  width: number;
  height: number;
}

function InnerChart({ dataPoints, width, height }: InnerChartProps) {
  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip<DataPoint>();

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  const xScale = useMemo(
    () =>
      scaleTime({
        domain: [
          getDate(dataPoints[0]),
          getDate(dataPoints[dataPoints.length - 1]),
        ],
        range: [0, innerWidth],
      }),
    [dataPoints, innerWidth],
  );

  const yScale = useMemo(
    () =>
      scaleLinear({
        domain: [
          0,
          dataPoints.reduce((m, d) => Math.max(m, getValue(d)), 0) * 1.1 || 1,
        ],
        range: [innerHeight, 0],
        nice: true,
      }),
    [dataPoints, innerHeight],
  );

  const handleTooltip = useCallback(
    (
      event:
        | React.TouchEvent<SVGRectElement>
        | React.MouseEvent<SVGRectElement>,
    ) => {
      const point = localPoint(event);
      if (!point) return;

      const x0 = xScale.invert(point.x - MARGIN.left);
      const idx = bisectDate(dataPoints, x0, 1);
      const d0 = dataPoints[Math.max(0, idx - 1)];
      const d1 = dataPoints[Math.min(idx, dataPoints.length - 1)];
      const d =
        d1 &&
        Math.abs(x0.getTime() - getDate(d0).getTime()) >
          Math.abs(getDate(d1).getTime() - x0.getTime())
          ? d1
          : d0;

      showTooltip({
        tooltipData: d,
        tooltipLeft: xScale(getDate(d)) + MARGIN.left,
        tooltipTop: yScale(getValue(d)) + MARGIN.top,
      });
    },
    [dataPoints, xScale, yScale, showTooltip],
  );

  if (innerWidth <= 0 || innerHeight <= 0) return null;

  return (
    <div className={styles.chartWrapper}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label="MCP interactions over time"
      >
        <Group left={MARGIN.left} top={MARGIN.top}>
          <GridRows
            scale={yScale}
            width={innerWidth}
            stroke="var(--colours-neutral-100, #f3f4f6)"
          />
          <AreaClosed
            data={dataPoints}
            x={(d) => xScale(getDate(d))}
            y={(d) => yScale(getValue(d))}
            yScale={yScale}
            curve={curveMonotoneX}
            fill={AREA_COLOR}
          />
          <LinePath
            data={dataPoints}
            x={(d) => xScale(getDate(d))}
            y={(d) => yScale(getValue(d))}
            curve={curveMonotoneX}
            stroke={LINE_COLOR}
            strokeWidth={2}
          />
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            numTicks={Math.min(dataPoints.length, 8)}
            tickFormat={(d) =>
              formatShortDate((d as Date).toISOString().slice(0, 10))
            }
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "middle" as const,
            })}
          />
          <AxisLeft
            scale={yScale}
            numTicks={5}
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "end" as const,
              dx: -4,
              dy: 4,
            })}
          />
          <Bar
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onTouchStart={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseMove={handleTooltip}
            onMouseLeave={hideTooltip}
          />
          {tooltipData && (
            <circle
              cx={xScale(getDate(tooltipData))}
              cy={yScale(getValue(tooltipData))}
              r={4}
              fill={LINE_COLOR}
              stroke="#fff"
              strokeWidth={2}
              pointerEvents="none"
            />
          )}
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip top={tooltipTop ?? 0} left={tooltipLeft ?? 0}>
          <div className={styles.tooltipContent}>
            <span className={styles.tooltipDate}>
              {formatShortDate(tooltipData.period_start)}
              {" \u2013 "}
              {formatShortDate(tooltipData.period_end)}
            </span>
            <strong>
              {formatNumber(tooltipData.interactions)} interactions
            </strong>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}

export interface McpInteractionTimeseriesChartProps {
  data: McpInteractionTimeseriesResponse;
}

const CHART_HEIGHT = 300;

export function McpInteractionTimeseriesChart({
  data,
}: McpInteractionTimeseriesChartProps) {
  if (data.data_points.length === 0) {
    return (
      <div className={styles.emptyState}>
        No MCP interaction data for this period.
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.statsRow}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Total Interactions</span>
          <span className={styles.statValue}>
            {formatCompact(data.total_interactions)}
          </span>
        </div>
      </div>
      <ParentSize>
        {({ width }) => (
          <InnerChart
            dataPoints={data.data_points}
            width={width}
            height={CHART_HEIGHT}
          />
        )}
      </ParentSize>
    </div>
  );
}
